import { Timestamp } from "firebase/firestore";
import { File } from "./file";

export interface ProjectSummary {
  id: string;
  status: string;
  org_id: string;
  infos: {
    general: {
      name: string;
      num: string;
      city?: string;
      creation_date: Timestamp;
      photo_file?: { url: string };
      coordinates?: { latitude: number; longitude: number };
    };
    team: {
      commercial_id?: string;
      conductor_id?: string;
      comptability_id?: string;
    };
    client?: {
      contacts: ContactInfos[];
    };
  };
  last_completed_cp?: string;
  current_step?: string;
  unseen_by_user?: Record<string, { photos?: number; documents?: number; emails?: number; signatures?: number }>;
}

export interface ProjectBillingStep {
  label: string;
  /** Short label snapshotted from the billing template at creation. */
  abbreviation?: string;
  percentage: number;
  montant: number;
  is_demarrage: boolean;
  linked_step_ids: string[];
  status: "pending" | "creating" | "draft" | "finalized" | "sent" | "paid" | "error";
  invoice_id?: number;
  invoice_number?: string;
  invoice_pdf_url?: string;
  drafted_at?: string;
  finalized_at?: string;
  sent_at?: string;
  sent_dates?: string[];
  paid_at?: string;
  invoice_error?: string;
  billing_warning?: string;
  /**
   * Legacy: written by both scheduler alerts and manual relances before
   * the field split. Read for backwards-compat fallback only — new code
   * writes either `last_conductor_alert_sent_at` or `last_relance_sent_at`.
   */
  last_alert_sent_at?: string;
  /** Set by the scheduler when a retard alert is emailed to the conducteur. */
  last_conductor_alert_sent_at?: string;
  /** Set ONLY when a human clicks "Relancer". Surfaced in the UI as
   * "Relance demandée le …". */
  last_relance_sent_at?: string;
  payment_delay_days?: number;
  project_delay_days?: number;
  step_gap_delay_days?: number;
}

export interface BillingFlags {
  has_late: boolean;
  overdue_count: number;
  has_project_delay: boolean;
  total_paid: number;
  total_invoiced: number;
  next_due_date?: string;
  last_computed_at?: string;
}

export interface ProjectBilling {
  billing_template_id?: string;
  montant_total: number;
  steps: ProjectBillingStep[];
  pennylane_customer_id?: number;
  planning_end_date?: string;
  billing_flags?: BillingFlags & { has_step_gap?: boolean };
}

export interface Project {
  id: string;
  infos: ProjectInfos;

  org_id: string;
  author_id: string;
  history_events: ProjectHistoryEvent[];
  themes: ProjectTheme[];
  photos: ProjectPhoto[];
  folders: ProjectFolder[];

  status: string;
  guest_user_ids?: string[];
  current_step?: string;
  last_completed_cp?: string;
  billing?: ProjectBilling;
  unseen_by_user?: Record<string, { photos?: number; documents?: number; emails?: number; signatures?: number }>;
}

export interface ProjectInfos {
  general: GeneralInfos;
  client: ClientInfos;
  team: TeamInfos;
  partners: PartnerInfos[];
}

export interface GeneralInfos {
  name: string;
  num: string;
  address?: string;
  city?: string;
  postal?: string;
  country?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  home_address?: string;
  home_city?: string;
  home_postal?: string;
  lot?: string;
  section?: string;
  cadastre_number?: string;

  creation_date: Timestamp;
  last_updated: Timestamp;
  photo_file?: File;
  notes?: string;
}

export interface ClientInfos {
  contacts: ContactInfos[];
  last_activity_date?: Timestamp;
}
export interface ContactInfos {
  first_name: string;
  last_name: string;
  prefix: "M." | "Mme" | "SCI";
  phone_number?: string;
  email?: string;
  user_id?: string;
  sci_name?: string;
}

export interface TeamInfos {
  commercial_id?: string;
  conductor_id?: string;
  dessinateur_id?: string;
  sav_id?: string;
  comptability_id?: string;
  communication_id?: string;
  visible_member_ids?: string[];
}
export interface PartnerDecennale {
  start_date: string; // ISO date "2025-03-15"
  end_date: string;   // ISO date "2026-03-14"
  gcs_path: string;
  url?: string;
  /** @deprecated Legacy field — use start_date/end_date instead */
  year?: number;
}

export interface PartnerInfos {
  id: string;
  role: string;
  display_name: string;
  phone_number?: string;
  email?: string;
  company_name?: string;
  company_phone?: string;
  company_address?: string;
  company_city?: string;
  company_postal?: string;
  company_email?: string;
  decennales?: PartnerDecennale[];
  company_types: string[];
}

export interface ProjectHistoryEvent {
  id: string;
  date: Timestamp;
  user: {
    id: string;
    display_name: string;
    photo_url?: string;
  };
  action: string;
  photos_id?: string[]; // Array of photo URLs or IDs related to the action
  documents_id?: string[]; // Array of documents related to the action
  control_point?: {
    id: string;
    name: string;
    new_status: string;
  };
  email?: {
    email_sent_id: string;
    template_id: string;
    sent: boolean;
    subject: string;
    body: string;
  };
  signature_request_id?: string;
}

export type ProjectTheme = {
  id: string;
  label: string;
  steps: Step[];
};

export type Step = {
  id: string;
  label: string;
  controlpoints: ControlPoint[];
};

export type ControlPoint = {
  id: string;
  label: string;
  state: "default" | "completed" | "missed" | "not_applicable";
  photos_id?: string[]; // Array of photo URLs or IDs related to the control point
  documents_id?: string[]; // Array of documents related to the control point
  associated_email_template_id?: string; // Optional field for the email template associated with the control point
  email?: {
    sent?: boolean; // Optional field to indicate if the email has been sent for this control point
    date?: Timestamp; // Optional field to store the date when the email was sent for this control point
    email_sent_id?: string; // Optional field to store the ID of the sent email related to this control point
  };
  hidden?: boolean; // If true, hidden from guest/client view
};

export interface ProjectPhoto {
  id: string;
  file: File;
  description?: string;
  geo_location?: {
    latitude: number;
    longitude: number;
  };
  hidden?: boolean;
}

export interface ProjectFolder {
  id: string;
  name: string;
  documents?: ProjectDocument[];
  sub_folders?: ProjectSubFolder[];
  /**
   * When true, the project's conducteur receives a push notification each
   * time a document is uploaded into this folder.
   */
  notify_conductor_on_upload?: boolean;
}

export interface ProjectSubFolder {
  id: string;
  name: string;
  documents?: ProjectDocument[];
  /**
   * When true, the project's conducteur receives a push notification each
   * time a document is uploaded into this subfolder.
   */
  notify_conductor_on_upload?: boolean;
}
export interface ProjectDocument {
  id: string;
  name: string;
  file: File;
  hidden?: boolean;
}

// create a Template type that has the same structure as Project but with a few modifications
export interface Template extends Project {
  order?: number;
}

/**
 * Build the display name for a project from its client contacts.
 * SCI: "SCI Alpha - DUPONT Jean, Marie"
 * Non-SCI: "DUPONT Jean, Marie"
 * Fallback: project general name
 */
export const getProjectDisplayName = (project: { infos: { general: { name: string }; client?: { contacts: ContactInfos[] } } }): string => {
  const contacts = project.infos.client?.contacts;
  if (!contacts || contacts.length === 0) return project.infos.general.name;

  const firstContact = contacts[0];
  const isSci = firstContact.prefix === "SCI" && firstContact.sci_name;

  // Build contact names: "DUPONT Jean, Marie, ..."
  const firstNames = contacts.map((c) => c.first_name).filter(Boolean);
  const lastName = firstContact.last_name || "";
  const contactPart = lastName
    ? `${lastName} ${firstNames.join(", ")}`
    : firstNames.join(", ");

  if (!contactPart) return project.infos.general.name;

  return isSci
    ? `SCI ${firstContact.sci_name} - ${contactPart}`
    : contactPart;
};
